"use client";

// Single-policy version of the Board Packet journey view (part-2 notes §1.2):
// what a Kanban card click shows once a policy is past Collation — the
// statement-level record of what changed and who changed it, plus route-gate
// status. Read-only here; gate approvals/route/board-involvement are still
// edited in AssignmentModal, reachable via "Manage governance" below (they
// stay a live action through Applied -> Proposed -> Ratified, not just the
// pre-Submitted assignment stages).

import { useEffect, useState } from "react";
import Link from "next/link";
import { getPolicyJourney } from "@/lib/policies/api";
import { StatusChip } from "../StatusChip";
import { strip } from "../theme";

export function PolicyJourneyModal({ policy, onClose, onManageGovernance }: { policy: any; onClose: () => void; onManageGovernance?: () => void }) {
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getPolicyJourney(policy.id).then(setData).catch((e) => setError(e.message));
    }, [policy.id]);

    return (
        <div onClick={onClose} className="fixed inset-0 z-[100] bg-[rgba(12,34,38,0.45)] flex items-center justify-center p-6">
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-[640px] max-w-full shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
                <div className="border-b border-[#eef1ef] flex items-start justify-between gap-3" style={{ padding: "22px 26px" }}>
                    <div>
                        <div className="text-[11px] tracking-wide font-bold text-[#88a39b]">POLICY JOURNEY</div>
                        <div className="font-bold text-lg mt-1 text-[#15302f]">{policy.id} · {policy.name}</div>
                        <div className="flex items-center gap-2 mt-1.5">
                            <StatusChip state={policy.lifecycle_state} />
                            {data?.assignment?.steward_name && (
                                <span className="text-xs text-[#5f7173]">Steward: {data.assignment.steward_name}</span>
                            )}
                        </div>
                    </div>
                    <div className="flex-none flex items-center gap-2">
                        {onManageGovernance && (
                            <button
                                onClick={onManageGovernance}
                                className="border border-[#4b8892] bg-white text-[#3a6b78] text-[11.5px] font-bold rounded-lg px-3 py-1.5"
                                title="Set approval route / board involvement, record gate approvals"
                            >
                                Manage governance
                            </button>
                        )}
                        <Link
                            href={`/dashboard/policies/workspace/${policy.id}`}
                            className="border border-[#4b8892] text-[#3a6b78] text-[11.5px] font-bold rounded-lg px-3 py-1.5 no-underline"
                            title="Staff can view and edit any policy workspace"
                        >
                            Open workspace
                        </Link>
                    </div>
                </div>

                <div className="overflow-y-auto" style={{ padding: "22px 26px" }}>
                    {error && (
                        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">{error}</div>
                    )}
                    {!data && !error ? (
                        <div className="py-8 flex justify-center">
                            <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                        </div>
                    ) : data && (
                        <>
                            {data.gates?.length > 0 && (
                                <div className="flex gap-2 flex-wrap mb-4">
                                    {data.gates.map((g: any) => (
                                        <span key={g.gate}
                                            className={`text-[10.5px] font-bold rounded px-2 py-1 ${g.approved ? "bg-[#dceee7] text-[#2c7a63]" : "bg-[#fbecd6] text-[#9a6a16]"}`}>
                                            {g.approved ? "✓" : "○"} {g.label}{g.approved && g.approved_by ? ` — ${g.approved_by}` : ""}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="flex flex-col gap-3">
                                {data.statements.map((s: any) => (
                                    <div key={s.sid} className="border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="px-4 py-2.5 bg-[#f6f8f7] flex items-center gap-2 border-b border-[#eef1ef]">
                                            <span className="text-[11px] font-mono font-bold text-[#88a39b]">{s.sid}</span>
                                            {s.changed ? (
                                                <span className="text-[9.5px] font-bold bg-[#e6e9f2] text-[#4d5aa0] rounded px-1.5 py-0.5">
                                                    CHANGED · v{s.version}{s.changed_by.length > 0 && ` · by ${Array.from(new Set(s.changed_by)).join(", ")}`}
                                                </span>
                                            ) : (
                                                <span className="text-[9.5px] font-bold bg-[#ece9e2] text-[#7a786f] rounded px-1.5 py-0.5">UNCHANGED</span>
                                            )}
                                        </div>
                                        <div className="px-4 py-3">
                                            {s.changed && (
                                                <p className="text-[12.5px] leading-relaxed text-[#9a4b4b] line-through opacity-80 mb-1">{strip(s.original_text)}</p>
                                            )}
                                            <p className="text-[13px] leading-relaxed text-[#2c3e40]">{strip(s.current_text)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div className="bg-[#f4f6f5] border-t border-[#e6ebe9] flex justify-end gap-2.5" style={{ padding: "16px 26px" }}>
                    <button onClick={onClose} className="border border-[#d9dedb] bg-white text-[#5f7173] text-[13px] font-bold rounded-lg px-4 py-2">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
